import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { X, Mail, CheckCircle2, Building2, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import logoDarkPurple from '../imports/1_(3).svg';
import {
  buildSignupMagicLinkBody,
  sendSignupMagicLinkEmail,
  type SignupAccountType,
} from '@/api/beatlyApi';
import { isValidEmail, normalizeEmailFromUrlQuery } from '@/utils/validation';
import { showError, showSuccess } from '@/utils/alert';

interface SignupPageProps {
  onClose?: () => void;
}

/* ─── Step Indicator ─── */
const StepIndicator: React.FC<{ currentStep: number }> = ({ currentStep }) => {
  const steps = [1, 2, 3];
  return (
    <div className="flex items-center justify-center gap-0 my-8">
      {steps.map((step, i) => (
        <React.Fragment key={step}>
          <div className="flex items-center justify-center">
            {currentStep > step ? (
              <div className="w-6 h-6 rounded-full bg-[#a9f600] flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-white" />
              </div>
            ) : currentStep === step ? (
              <div className="w-3 h-3 rounded-full bg-[#a9f600]" />
            ) : (
              <div className="w-3 h-3 rounded-full bg-[#d0d5dd]" />
            )}
          </div>
          {i < steps.length - 1 && (
            <div className="w-16 h-[2px] mx-1">
              <div
                className="h-full rounded-full transition-colors duration-300"
                style={{
                  background: currentStep > step ? '#a9f600' : '#e4e7ec',
                }}
              />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export const SignupPage: React.FC<SignupPageProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [accountType, setAccountType] = useState<SignupAccountType | ''>('');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState(() => {
    const raw = searchParams.get('email');
    return raw != null ? normalizeEmailFromUrlQuery(raw) : '';
  });
  const [existingAccount, setExistingAccount] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const firstNameInputRef = useRef<HTMLInputElement>(null);

  const emailFromUrl = searchParams.get('email');
  const title = emailFromUrl ? t('signup.titleFromLogin') : t('signup.title');
  const subtitle = useMemo(() => {
    if (emailFromUrl) return t('signup.subtitleFromLogin');
    if (accountType === 'advertiser') return t('signup.subtitleAdvertiser');
    if (accountType === 'creator') return t('signup.subtitleInfluencer');
    return t('signup.subtitle');
  }, [emailFromUrl, accountType, t]);

  const canProceed =
    (step === 1 && accountType !== '') ||
    (step === 2 && firstName.trim() !== '' && email.trim() !== '');

  const submitContactStep = useCallback(async () => {
    if (accountType !== 'advertiser' && accountType !== 'creator') return;
    if (!firstName.trim() || !email.trim()) return;
    const trimmedEmail = email.trim();
    if (!isValidEmail(trimmedEmail)) {
      showError(t('validation.invalidEmail'));
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await sendSignupMagicLinkEmail(
        buildSignupMagicLinkBody(trimmedEmail, firstName, accountType),
      );
      setExistingAccount(res.code === 'SENT_LOGIN_MAGICLINK');
      setStep(3);
    } catch {
      showError(t('signup.errorGeneric'));
    } finally {
      setIsSubmitting(false);
    }
  }, [accountType, email, firstName, t]);

  const handleNext = useCallback(() => {
    if (step === 1 && accountType) {
      setStep(2);
      return;
    }
    if (step === 2 && canProceed) {
      void submitContactStep();
    }
  }, [step, accountType, canProceed, submitContactStep]);

  useEffect(() => {
    if (step > 2) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && canProceed && !isSubmitting) {
        e.preventDefault();
        handleNext();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [step, canProceed, isSubmitting, handleNext]);

  useEffect(() => {
    if (step === 2 && emailFromUrl) {
      const id = setTimeout(() => {
        firstNameInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(id);
    }
  }, [step, emailFromUrl]);

  const onSubmitContact = useCallback(() => {
    if (step === 2 && canProceed && !isSubmitting) {
      void handleNext();
    }
  }, [step, canProceed, isSubmitting, handleNext]);

  const handleResend = () => {
    if (accountType !== 'advertiser' && accountType !== 'creator') return;
    if (!firstName.trim() || !email.trim()) return;
    const trimmedEmail = email.trim();
    if (!isValidEmail(trimmedEmail)) {
      showError(t('validation.invalidEmail'));
      return;
    }
    setIsSubmitting(true);
    sendSignupMagicLinkEmail(buildSignupMagicLinkBody(trimmedEmail, firstName, accountType))
      .then(() => showSuccess(t('signup.emailResent')))
      .catch(() => showError(t('signup.errorGeneric')))
      .finally(() => setIsSubmitting(false));
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center overflow-auto"
      style={{ background: '#f8f9fa', animation: 'signupFadeIn 300ms ease-out' }}
    >
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-9 h-9 rounded-full flex items-center justify-center text-[#98a2b3] hover:text-[#344054] hover:bg-white transition-all z-10"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      <div
        className="w-full max-w-[480px] mx-4"
        style={{ animation: 'signupContentIn 500ms ease-out' }}
      >
        <div className="flex justify-center mb-8">
          <Link to="/login">
            <img src={logoDarkPurple} alt="Beatly" className="h-9 w-auto" />
          </Link>
        </div>

        <div
          className="bg-white rounded-[24px] px-10 py-10"
          style={{
            boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 24px rgba(0,0,0,0.06), 0 12px 48px rgba(0,0,0,0.04)',
          }}
        >
          <div className="text-center">
            <h1
              className="text-[#101828] text-[26px] mb-2"
              style={{ fontFamily: 'Instrument Sans, sans-serif', fontWeight: 600 }}
            >
              {title}
            </h1>
            <p className="text-[#667085] text-[15px] leading-relaxed">{subtitle}</p>
          </div>

          <StepIndicator currentStep={step} />

          <div style={{ animation: 'signupStepIn 300ms ease-out' }} key={step}>
            {step === 1 && (
              <StepOne accountType={accountType} onAccountTypeChange={setAccountType} />
            )}
            {step === 2 && (
              <StepTwo
                firstName={firstName}
                email={email}
                onFirstNameChange={setFirstName}
                onEmailChange={setEmail}
                focusedField={focusedField}
                onFocus={setFocusedField}
                emailInputRef={emailInputRef}
                firstNameInputRef={firstNameInputRef}
                onSubmitStep={onSubmitContact}
              />
            )}
            {step === 3 && (
              <StepThree
                onResend={handleResend}
                isResending={isSubmitting}
                existingAccount={existingAccount}
              />
            )}
          </div>

          {step < 3 && (
            <button
              onClick={handleNext}
              disabled={!canProceed || isSubmitting}
              className="w-full py-3.5 rounded-xl text-white text-[13px] tracking-[0.08em] transition-all active:scale-[0.98] flex items-center justify-center mt-8 disabled:opacity-40"
              style={{
                fontWeight: 600,
                textTransform: 'uppercase' as const,
                background: canProceed && !isSubmitting ? '#be43ff' : '#d0d5dd',
                boxShadow: canProceed && !isSubmitting ? '0 4px 16px rgba(190,67,255,0.25)' : 'none',
              }}
            >
              {step === 2 && isSubmitting ? t('signup.sending') : t('signup.next')}
            </button>
          )}
        </div>
        <p className="text-center text-[14px] text-[#667085] mt-4">
          {t('signup.hasAccount')}{' '}
          <Link to="/login" className="text-[#be43ff] hover:text-[#a830e0]" style={{ fontWeight: 600 }}>
            {t('signup.logIn')}
          </Link>
        </p>
      </div>

      <style>{`
        @keyframes signupFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes signupContentIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes signupStepIn {
          from { opacity: 0; transform: translateX(12px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   Step 1 — Role selection
   ═══════════════════════════════════════════════ */
interface StepOneProps {
  accountType: SignupAccountType | '';
  onAccountTypeChange: (value: SignupAccountType) => void;
}

const StepOne: React.FC<StepOneProps> = ({ accountType, onAccountTypeChange }) => {
  const { t } = useTranslation();

  const roles: { value: SignupAccountType; icon: React.ReactNode; labelKey: string }[] = [
    { value: 'advertiser', icon: <Building2 className="w-8 h-8 text-[#101828]" />, labelKey: 'signup.type.advertiser' },
    { value: 'creator', icon: <Sparkles className="w-8 h-8 text-[#101828]" />, labelKey: 'signup.type.influencer' },
  ];

  return (
    <div>
      <h2 className="text-[#101828] text-[16px] mb-5" style={{ fontWeight: 600 }}>
        {t('signup.step1Title')}
      </h2>
      <div className="space-y-3">
        {roles.map(({ value, icon, labelKey }) => {
          const selected = accountType === value;
          return (
            <label
              key={value}
              className="flex items-center gap-4 w-full px-4 py-4 rounded-xl border cursor-pointer transition-all duration-200"
              style={{
                borderColor: selected ? '#be43ff' : '#e4e7ec',
                background: selected ? 'rgba(190, 67, 255, 0.04)' : '#fff',
                boxShadow: selected ? '0 0 0 3px rgba(190,67,255,0.08)' : 'none',
              }}
            >
              <input
                type="radio"
                name="account_type"
                value={value}
                checked={selected}
                onChange={() => onAccountTypeChange(value)}
                className="sr-only"
              />
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#f8f9fa] flex items-center justify-center">
                {icon}
              </div>
              <span className="text-[#101828] text-[15px] flex-1" style={{ fontWeight: 600 }}>
                {t(labelKey)}
              </span>
              {selected && (
                <CheckCircle2 className="w-5 h-5 text-[#be43ff] flex-shrink-0" />
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   Step 2 — Contact data (name + email)
   ═══════════════════════════════════════════════ */
interface StepTwoProps {
  firstName: string;
  email: string;
  onFirstNameChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  focusedField: string | null;
  onFocus: (v: string | null) => void;
  emailInputRef: React.RefObject<HTMLInputElement | null>;
  firstNameInputRef: React.RefObject<HTMLInputElement | null>;
  onSubmitStep: () => void;
}
const StepTwo: React.FC<StepTwoProps> = ({
  firstName, email, onFirstNameChange, onEmailChange, focusedField, onFocus,
  emailInputRef, firstNameInputRef, onSubmitStep,
}) => {
  const { t } = useTranslation();
  return (
  <div>
    <h2 className="text-[#101828] text-[16px] mb-5" style={{ fontWeight: 600 }}>
      {t('signup.step2Title')}
    </h2>
    <div className="space-y-5">
      <div>
        <label className="block text-[13px] text-[#344054] mb-1.5" style={{ fontWeight: 500 }}>
          {t('signup.firstNameLabel')} <span className="text-[#be43ff]">*</span>
        </label>
        <input
          ref={firstNameInputRef}
          type="text"
          value={firstName}
          onChange={(e) => onFirstNameChange(e.target.value)}
          onFocus={() => onFocus('firstName')}
          onBlur={() => onFocus(null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              emailInputRef.current?.focus();
            }
          }}
          placeholder={t('signup.firstNamePlaceholder')}
          className="w-full px-4 py-3 bg-white text-[15px] text-[#101828] placeholder:text-[#d0d5dd] outline-none rounded-xl border transition-all duration-200"
          style={{
            borderColor: focusedField === 'firstName' ? '#be43ff' : '#e4e7ec',
            boxShadow: focusedField === 'firstName' ? '0 0 0 3px rgba(190,67,255,0.08)' : 'none',
          }}
          required
        />
      </div>

      <div>
        <label className="block text-[13px] text-[#344054] mb-1.5" style={{ fontWeight: 500 }}>
          {t('signup.emailLabel')} <span className="text-[#be43ff]">*</span>
        </label>
        <input
          ref={emailInputRef}
          type="email"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          onFocus={() => onFocus('email')}
          onBlur={() => onFocus(null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSubmitStep();
            }
          }}
          placeholder={t('signup.emailPlaceholder')}
          className="w-full px-4 py-3 bg-white text-[15px] text-[#101828] placeholder:text-[#d0d5dd] outline-none rounded-xl border transition-all duration-200"
          style={{
            borderColor: focusedField === 'email' ? '#be43ff' : '#e4e7ec',
            boxShadow: focusedField === 'email' ? '0 0 0 3px rgba(190,67,255,0.08)' : 'none',
          }}
          required
        />
      </div>
    </div>
  </div>
  );
};

/* ═══════════════════════════════════════════════
   Step 3 — Email verification
   ═══════════════════════════════════════════════ */
interface StepThreeProps {
  onResend: () => void;
  isResending: boolean;
  existingAccount: boolean;
}
const StepThree: React.FC<StepThreeProps> = ({ onResend, isResending, existingAccount }) => {
  const { t } = useTranslation();
  return (
    <div>
      <div className="rounded-xl border border-[#e4e7ec] px-8 py-10 text-center">
        <div className="flex justify-center mb-5">
          <Mail className="w-8 h-8 text-[#101828]" />
        </div>

        <h2 className="text-[#101828] text-[16px] mb-3" style={{ fontWeight: 600 }}>
          {existingAccount ? t('signup.verifyEmailTitleExisting') : t('signup.verifyEmailTitle')}
        </h2>
        <p className="text-[#667085] text-[14px] leading-relaxed max-w-[300px] mx-auto mb-5">
          {existingAccount ? t('signup.verifyEmailSubtitleExisting') : t('signup.verifyEmailSubtitle')}
        </p>
        <p className="text-[#667085] text-[13px]">
          {t('signup.noEmailOrExpired')}{' '}
          <button
            type="button"
            onClick={onResend}
            disabled={isResending}
            className="text-[#101828] underline underline-offset-2 hover:text-[#be43ff] transition-colors disabled:opacity-70"
            style={{ fontWeight: 600 }}
          >
            {t('signup.requestNewLink')}
          </button>
        </p>
      </div>
    </div>
  );
};
